import { 
    Box,
    Container,
    Grid,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Backdrop,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment
} from '@mui/material';
import UserContext from "Context/UserProvider";
import { useContext, useEffect, useRef, useState } from "react";
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from "react-router-dom";
import { isChargePointOperator } from "utils/categoryUtils";
import SaveIcon from '@mui/icons-material/Save';
import DashboardCard from 'components/dashboard/common/DashboardCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { useApiService } from "../../services/ApiService/ApiService";

interface RateCardParameter {
    uuid?: string;
    name?: string;
    allowanceKWh?: number;
    energyCostAcSinglePhaseGbpPerKWhOffPeak?: number;
    energyCostAcThreePhaseGbpPerKWhOffPeak?: number;
    energyCostDcGbpPerKWhOffPeak?: number;
    energyCostAcSinglePhaseGbpPerKWhPeak?: number;
    energyCostAcThreePhaseGbpPerKWhPeak?: number;
    energyCostDcGbpPerKWhPeak?: number;
}

function ChargingPartnerRateCardEditPage(props: any) {
    const [loading, setLoading] = useState(true);
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [parameter, setParameter] = useState<RateCardParameter>({});
    const location = useLocation();
    const params = location.state as any;
    const currentInput = useRef() as any;
    const [isDisabled, setIsDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    // Add CSS to hide spinner arrows
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            input[type="number"] {
                -moz-appearance: textfield;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    useEffect(() => {
        console.log("rcuuid " + params.chargingPartnerRateCardUuid);

        if (params.chargingPartnerRateCardUuid == undefined || params.chargingPartnerRateCardUuid == null) {
            setLoading(false);
        } else {
            fetchChargingPartnerRateCard();
        }
    }, []);

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = e.target;
        setParameter((prev: RateCardParameter) => ({
            ...prev,
            [name]: value
        }));
    }

    async function fetchChargingPartnerRateCard() {
        let bodyData: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["businessUuid"] = userInfo.businessUuid;
        bodyData["chargingPartnerRateCardUuid"] = params.chargingPartnerRateCardUuid;

        try {
            const response = await fetchWithAuth('/charging-partner/rate-card/get', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch rate card');
            }

            const res_json = await response.json();
            setParameter(res_json.chargingPartnerRateCard);
        } catch (error) {
            console.error('Error fetching rate card:', error);
            toast.error('Failed to fetch rate card data');
        } finally {
            setLoading(false);
        }
    }

    async function saveChargingPartnerRateCard() {
        setIsDisabled(true);
        let bodyData: { [name: string]: any } = {};
        let chargingPartnerRateCard: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["chargingPartnerRateCard"] = chargingPartnerRateCard;
        bodyData["chargingPartnerRateCardUuid"] = parameter.uuid;
        bodyData["businessUuid"] = userInfo.businessUuid;

        chargingPartnerRateCard["energyCostAcSinglePhaseGbpPerKWhOffPeak"] = Number(parameter.energyCostAcSinglePhaseGbpPerKWhOffPeak);
        chargingPartnerRateCard["energyCostAcThreePhaseGbpPerKWhOffPeak"] = Number(parameter.energyCostAcThreePhaseGbpPerKWhOffPeak);
        chargingPartnerRateCard["energyCostDcGbpPerKWhOffPeak"] = Number(parameter.energyCostDcGbpPerKWhOffPeak);
        chargingPartnerRateCard["energyCostAcSinglePhaseGbpPerKWhPeak"] = Number(parameter.energyCostAcSinglePhaseGbpPerKWhPeak);
        chargingPartnerRateCard["energyCostAcThreePhaseGbpPerKWhPeak"] = Number(parameter.energyCostAcThreePhaseGbpPerKWhPeak);
        chargingPartnerRateCard["energyCostDcGbpPerKWhPeak"] = Number(parameter.energyCostDcGbpPerKWhPeak);
        chargingPartnerRateCard["name"] = parameter.name;
        chargingPartnerRateCard["allowanceKWh"] = parameter.allowanceKWh;

        try {
            const response = await fetchWithAuth('/charging-partner/rate-card/save', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            const res_json = await response.json();

            if (response.ok) {
                toast.success('Rate Card Save Successful', {
                    position: 'bottom-center',
                    duration: 5000,
                });
                navigate('/share/business/charging-partners/rate-cards');
            } else if (response.status === 400) {
                setErrorMessage("An error (" + res_json.code + ") occurred whilst saving this rate card.");
            }
        } catch (error) {
            console.error('Error saving rate card:', error);
            toast.error('Failed to save rate card');
        } finally {
            setIsDisabled(false);
        }
    }

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', bgcolor: 'background.default', pt: 1.5, pb: 3, pr: 3 }}>
            <Backdrop open={isDisabled} sx={{ zIndex: 1300 }}>
                <CircularProgress />
            </Backdrop>

            <Container maxWidth="xl" sx={{ px: 0 }}>
                <Grid container spacing={3}>
                    {isChargePointOperator(userInfo.businessCategory) ? (
                        <Grid item xs={12} md={6}>
                            <DashboardCard
                                title="Rate Card Details"
                                icon={<AccountBalanceWalletIcon />}
                                accentColor="primary"
                                noPadding
                            >
                                <Box sx={{ height: 400, p: 3 }}>
                                    <Grid container spacing={0}>
                                        <Grid item xs={12} sx={{ mb: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Name"
                                                name="name"
                                                value={parameter.name || ''}
                                                onChange={onChange}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sx={{ mb: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Allowance kW/h"
                                                name="allowanceKWh"
                                                type="number"
                                                value={parameter.allowanceKWh || ''}
                                                onChange={onChange}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Price</InputLabel>
                                                <OutlinedInput
                                                    type="number"
                                                    name="energyCostDcGbpPerKWhPeak"
                                                    value={parameter.energyCostDcGbpPerKWhPeak || ''}
                                                    onChange={onChange}
                                                    startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                    label="Price"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DashboardCard>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} md={6}>
                                <DashboardCard
                                    title="Rate Card Details"
                                    icon={<BoltIcon />}
                                    accentColor="primary"
                                    noPadding
                                >
                                    <Box sx={{ height: 400, p: 3 }}>
                                        <Grid container spacing={0}>
                                            <Grid item xs={12} sx={{ mb: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Name"
                                                    name="name"
                                                    value={parameter.name || ''}
                                                    onChange={onChange}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Allowance kW/h"
                                                    name="allowanceKWh"
                                                    type="number"
                                                    value={parameter.allowanceKWh || ''}
                                                    onChange={onChange}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </DashboardCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DashboardCard
                                    title="Off-Peak Energy Cost"
                                    icon={<NightsStayIcon />}
                                    accentColor="info"
                                    noPadding
                                >
                                    <Box sx={{ height: 400, p: 3 }}>
                                        <Grid container spacing={0}>
                                            <Grid item xs={12} sx={{ mb: 3 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>AC Single Phase</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostAcSinglePhaseGbpPerKWhOffPeak"
                                                        value={parameter.energyCostAcSinglePhaseGbpPerKWhOffPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="AC Single Phase"
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sx={{ mb: 3 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>AC Three Phase</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostAcThreePhaseGbpPerKWhOffPeak"
                                                        value={parameter.energyCostAcThreePhaseGbpPerKWhOffPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="AC Three Phase"
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>DC</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostDcGbpPerKWhOffPeak"
                                                        value={parameter.energyCostDcGbpPerKWhOffPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="DC"
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </DashboardCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DashboardCard
                                    title="Peak Energy Cost"
                                    icon={<WbSunnyIcon />}
                                    accentColor="warning"
                                    noPadding
                                >
                                    <Box sx={{ height: 400, p: 3 }}>
                                        <Grid container spacing={0}>
                                            <Grid item xs={12} sx={{ mb: 3 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>AC Single Phase</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostAcSinglePhaseGbpPerKWhPeak"
                                                        value={parameter.energyCostAcSinglePhaseGbpPerKWhPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="AC Single Phase"
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sx={{ mb: 3 }}>
                                                <FormControl fullWidth>
                                                    <InputLabel>AC Three Phase</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostAcThreePhaseGbpPerKWhPeak"
                                                        value={parameter.energyCostAcThreePhaseGbpPerKWhPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="AC Three Phase"
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>DC</InputLabel>
                                                    <OutlinedInput
                                                        type="number"
                                                        name="energyCostDcGbpPerKWhPeak"
                                                        value={parameter.energyCostDcGbpPerKWhPeak || ''}
                                                        onChange={onChange}
                                                        startAdornment={<InputAdornment position="start">£</InputAdornment>}
                                                        label="DC"
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </DashboardCard>
                            </Grid>
                        </>
                    )}

                    {/* Save Button */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={saveChargingPartnerRateCard}
                                disabled={isDisabled}
                                size="large"
                            >
                                Save Rate Card
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default ChargingPartnerRateCardEditPage;
